/**
 * @package   EGI / Frontend — egi-card
 * @author    Frontend Specialist (AI) for Fabio Cherici
 * @version   1.0.0 (M-EGI-264 FASE B1)
 * @date      2026-06-11
 * @purpose   D3+D4 dieta egi-card: listener DELEGATI (QR share, enlarge viewer,
 *            drag-scroll utility gallery) al posto di onclick/<script> inline per-card.
 *            Un solo set di listener per pagina — fixa il bug storico dei listener
 *            duplicati N×M (querySelectorAll eseguito da ogni card renderizzata).
 */

import './egi-card.css';

interface QrShareLabels {
    modalTitle: string;
    scanToView: string;
    copyLink: string;
    linkCopied: string;
    downloadQr: string;
    close: string;
}

declare global {
    interface Window {
        /** Definita da public/js/egi-qr-share.js (window.egiQrShare = openQrModal) */
        egiQrShare?: (url: string, title: string, labels: QrShareLabels) => void;
        /** Definita dal blocco @once di egi-prism-3d.blade.php */
        openPrismViewer?: (egiId: number) => void;
    }
}

const QR_LABEL_KEYS: ReadonlyArray<keyof QrShareLabels> = [
    'modalTitle',
    'scanToView',
    'copyLink',
    'linkCopied',
    'downloadQr',
    'close',
];

function isQrShareLabels(value: unknown): value is QrShareLabels {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>; // narrow post-guard: solo lettura chiavi
    return QR_LABEL_KEYS.every((key) => typeof record[key] === 'string');
}

let cachedLabels: QrShareLabels | null = null;

/** Legge le 6 stringhe i18n emesse UNA volta dal Blade (@once, #egi-qr-share-i18n). */
function readQrShareLabels(): QrShareLabels | null {
    if (cachedLabels) {
        return cachedLabels;
    }
    const el = document.getElementById('egi-qr-share-i18n');
    if (!el || !el.textContent) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(el.textContent);
        if (isQrShareLabels(parsed)) {
            cachedLabels = parsed;
            return cachedLabels;
        }
    } catch {
        // JSON malformato: nessun modal QR, nessun crash (progressive enhancement).
    }
    return null;
}

/* ── Click delegati: QR share + enlarge viewer ─────────────────────────── */

document.addEventListener('click', (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    const qrButton = target.closest<HTMLElement>('button.egi-qr-btn[data-qr-url]');
    if (qrButton) {
        event.preventDefault();
        event.stopPropagation();
        const labels = readQrShareLabels();
        const url = qrButton.dataset.qrUrl ?? '';
        if (window.egiQrShare && labels && url !== '') {
            window.egiQrShare(url, qrButton.dataset.qrTitle ?? '', labels);
        }
        return;
    }

    const enlargeButton = target.closest<HTMLElement>('[data-egi-enlarge]');
    if (enlargeButton) {
        event.preventDefault();
        event.stopPropagation();
        const egiId = Number(enlargeButton.dataset.egiId);
        if (Number.isFinite(egiId) && window.openPrismViewer) {
            window.openPrismViewer(egiId);
        }
    }
});

/* ── Drag-scroll delegato: utility gallery (.utility-scroll-container) ──── */

interface DragState {
    container: HTMLElement;
    startX: number;
    scrollLeft: number;
}

let dragState: DragState | null = null;

document.addEventListener('mousedown', (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }
    const container = target.closest<HTMLElement>('.utility-scroll-container');
    if (!container) {
        return;
    }
    dragState = {
        container,
        startX: event.pageX - container.offsetLeft,
        scrollLeft: container.scrollLeft,
    };
});

document.addEventListener('mousemove', (event: MouseEvent): void => {
    if (!dragState) {
        return;
    }
    event.preventDefault();
    const x = event.pageX - dragState.container.offsetLeft;
    const walk = (x - dragState.startX) * 2;
    dragState.container.scrollLeft = dragState.scrollLeft - walk;
});

document.addEventListener('mouseup', (): void => {
    dragState = null;
});

export {};
